<template>
  <div class="space-y-6 mb-10">
    <div class="flex flex-row justify-between items-center bg-white p-4 rounded-lg border border-gray-200">
      <div>
        <h2 class="text-xl font-bold text-gray-900">{{ unitLabel }} Management</h2>
        <p class="text-sm text-gray-500">{{ subtitle }}</p>
      </div>
      <Button :label="`Create ${unitLabel}`" icon="pi pi-plus" size="small" @click="openCreateClassroom" />
    </div>

    <!-- Classrooms List -->
    <div v-if="loadingSchool" class="flex justify-center py-8">
      <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
    </div>

    <div v-else class="grid grid-cols-1 gap-6">
      <div v-for="classroom in validClassrooms" :key="classroom.id"
        class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <!-- Classroom Header -->
        <div class="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center group">
          <div class="flex items-center gap-3">
            <div v-if="editingClassroomId === classroom.id" class="flex items-center gap-2">
              <InputText v-model="editClassroomName" class="p-inputtext-sm font-semibold" autofocus
                @keydown.enter="saveClassroomEdit(classroom.id)" />
              <Button icon="pi pi-check" rounded text severity="success" size="small"
                @click="saveClassroomEdit(classroom.id)" :loading="isEditingClassroom" />
              <Button icon="pi pi-times" rounded text severity="secondary" size="small" @click="cancelClassroomEdit" />
            </div>
            <div v-else class="flex items-center gap-3">
              <h3 class="text-lg font-semibold text-gray-800">{{ classroom.name }}</h3>
              <Button
                icon="pi pi-pencil text-gray-400 group-hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                rounded text size="small" @click="startClassroomEdit(classroom)" />
            </div>
          </div>
          <span class="text-xs text-gray-500 font-medium px-2 py-1 bg-gray-200 rounded-full">{{
            (classroom.classroomSections || []).length }} Sections</span>
        </div>

        <!-- Sections Body -->
        <div class="p-6">
          <div class="flex flex-wrap gap-3 items-center">

            <!-- Existing Sections -->
            <div v-for="section in (classroom.classroomSections || []).filter(s => !!s)" :key="section!.id"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-primary/10 text-primary-700 border border-primary/20">
              <template v-if="editingSectionId === section!.id">
                <InputText v-model="editSectionName" class="p-inputtext-sm w-24" autofocus
                  @keydown.enter="saveSectionEdit(section!.id)" />
                <Button icon="pi pi-check" rounded text severity="success" size="small"
                  @click="saveSectionEdit(section!.id)" :loading="isEditingSection" />
                <Button icon="pi pi-times" rounded text severity="secondary" size="small"
                  @click="cancelSectionEdit" />
              </template>
              <template v-else>
                <span>{{ section!.name || section?.section?.name || '' }}</span>
                <Button icon="pi pi-pencil"
                  class="!p-1 !w-6 !h-6 !text-xs"
                  rounded text severity="secondary" size="small"
                  @click="startSectionEdit(section!)" />
              </template>
            </div>

            <!-- Empty State for Sections -->
            <span
              v-if="(!classroom.classroomSections || classroom.classroomSections.length === 0) && addingSectionTo !== classroom.id"
              class="text-sm text-gray-400 italic mr-2">
              No sections yet
            </span>

            <!-- Add Section Form (Inline) -->
            <div v-if="addingSectionTo === classroom.id" class="flex flex-col gap-2 min-w-[280px]">
              <div class="flex items-center gap-2 w-full">
                <Select
                  v-model="sectionInput"
                  :options="sectionOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select or type a section"
                  class="flex-1 min-w-[220px]"
                  :invalid="!!sectionError"
                  data-testid="section-picker"
                  filter
                  showClear
                  editable
                />

                <div class="flex items-center gap-2 shrink-0">
                  <Button icon="pi pi-check" rounded text severity="success" size="small"
                    :disabled="!canSaveSection"
                    data-testid="section-picker-save"
                    @click="saveNewSection(classroom.id)" :loading="isAddingSection" />
                  <Button icon="pi pi-times" rounded text severity="secondary" size="small" @click="cancelAddSection" />
                </div>
              </div>
              <small v-if="sectionError" class="text-red-500" data-testid="section-picker-error">{{ sectionError }}</small>
              <small v-else-if="sectionResolution.kind === 'new'" class="text-gray-500" data-testid="section-picker-hint">
                Creates a new section "{{ sectionResolution.name }}"
              </small>
            </div>

            <!-- Add Section Button -->
            <Button v-else label="Add Section" icon="pi pi-plus" size="small" outlined severity="secondary"
              class="!text-xs !py-1 !px-2" @click="startAddSection(classroom.id)" />

          </div>
        </div>
      </div>
    </div>

    <!-- Create Classroom Dialog -->
    <Dialog v-model:visible="createClassroomDialog" :header="`Create New ${unitLabel}`" :modal="true"
      class="p-fluid w-full max-w-md">
      <div class="field mt-2">
        <label for="new_classroom_name">{{ unitLabel }} Name</label>
        <InputText id="new_classroom_name" v-model="newClassroomName" class="w-full" :placeholder="namePlaceholder"
          autofocus />
      </div>
      <div class="field mt-4">
        <label for="new_classroom_abbr">Abbreviation (Optional)</label>
        <InputText id="new_classroom_abbr" v-model="newClassroomAbbr" class="w-full" :placeholder="abbrPlaceholder" />
      </div>

      <template #footer>
        <Button size="small" label="Cancel" icon="pi pi-times" text @click="createClassroomDialog = false" />
        <Button size="small" label="Create" icon="pi pi-check" @click="handleCreateClassroom" :loading="isCreatingClassroom" />
      </template>
    </Dialog>

  </div>
</template>

<script setup lang="ts">
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { extractGraphQLError } from '@/utils/graphqlError';
import { useToast } from 'primevue/usetoast';
import {
  canSaveSectionInput,
  classroomSectionName,
  resolveSectionInput,
  sectionInputMessage,
  selectableSectionOptions,
} from './sectionInput';

const toast = useToast();
const { classrooms, refetchSchool, loadingSchool, school_sections } = useSchoolQuery();
const { createClassroom, updateClassroom, createClassroomSection, updateClassroomSection, createSection } = useSchoolMutations();
const { isTertiary } = useSchoolFeatures();

// "Classroom" for primary/secondary, "Level" for tertiary. Single source of
// truth for every visible "classroom"/"level" string in this component.
const unitLabel = computed(() => (isTertiary.value ? 'Level' : 'Classroom'));
const subtitle = computed(() => (isTertiary.value
  ? 'Manage your institution\'s levels and their sections.'
  : 'Manage your school\'s classrooms and their sections (arms).'));
const namePlaceholder = computed(() => (isTertiary.value ? 'e.g. 100' : 'e.g. JSS 1'));
const abbrPlaceholder = computed(() => (isTertiary.value ? 'e.g. L1' : 'e.g. J1'));

const validClassrooms = computed(() => (classrooms.value || []).filter((c): c is NonNullable<typeof c> => !!c));
const availableSections = computed(() =>
  (school_sections.value || [])
    .filter((section): section is NonNullable<typeof section> => !!section && !!section.name)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
);

// --- Create Classroom State ---
const createClassroomDialog = ref(false);
const newClassroomName = ref('');
const newClassroomAbbr = ref('');
const isCreatingClassroom = ref(false);

const openCreateClassroom = () => {
  newClassroomName.value = '';
  newClassroomAbbr.value = '';
  createClassroomDialog.value = true;
};

const handleCreateClassroom = async() => {
  if (!newClassroomName.value.trim()) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Classroom name is required' });
    return;
  }

  isCreatingClassroom.value = true;
  try {
    await createClassroom({
      name: newClassroomName.value,
      abbreviation: newClassroomAbbr.value || undefined,
    });
    await refetchSchool();
    createClassroomDialog.value = false;
  } catch (e) {
    // handled in composable
  } finally {
    isCreatingClassroom.value = false;
  }
};

// --- Edit Classroom State ---
const editingClassroomId = ref<string | null>(null);
const editClassroomName = ref('');
const isEditingClassroom = ref(false);

const startClassroomEdit = (classroom: { id: string; name: string }) => {
  editingClassroomId.value = classroom.id;
  editClassroomName.value = classroom.name;
};

const cancelClassroomEdit = () => {
  editingClassroomId.value = null;
  editClassroomName.value = '';
};

const saveClassroomEdit = async(id: string) => {
  if (!editClassroomName.value.trim()) return;

  isEditingClassroom.value = true;
  try {
    await updateClassroom(id, { name: editClassroomName.value });
    await refetchSchool();
    editingClassroomId.value = null;
  } catch (e) {
    // handled
  } finally {
    isEditingClassroom.value = false;
  }
};

// --- Add Section State ---
const addingSectionTo = ref<string | null>(null); // classroom ID
// Section NAME — picked from the list or typed (showClear sets it to null).
const sectionInput = ref<string | null>('');
const sectionSaveError = ref('');
const isAddingSection = ref(false);

const addingClassroom = computed(() => validClassrooms.value.find((classroom) => classroom.id === addingSectionTo.value));
const attachedSectionIds = computed(() => new Set(
  (addingClassroom.value?.classroomSections || [])
    .map((classroomSection) => classroomSection?.section?.id)
    .filter((id): id is string => !!id)
    .map(String),
));
const sectionOptions = computed(() => selectableSectionOptions(availableSections.value, attachedSectionIds.value));
const sectionResolution = computed(() => resolveSectionInput(sectionInput.value, availableSections.value, attachedSectionIds.value));
const canSaveSection = computed(() => canSaveSectionInput(sectionResolution.value));
const sectionError = computed(() => sectionSaveError.value
  || (sectionResolution.value.kind === 'attached' ? sectionInputMessage(sectionResolution.value, addingClassroom.value?.name) : ''));

watch(sectionInput, () => {
  sectionSaveError.value = '';
});

const startAddSection = (classroomId: string) => {
  cancelAddSection(); // close others
  addingSectionTo.value = classroomId;
};

const cancelAddSection = () => {
  addingSectionTo.value = null;
  sectionInput.value = '';
  sectionSaveError.value = '';
};

const saveNewSection = async(classroomId: string) => {
  const resolution = sectionResolution.value;
  if (!canSaveSectionInput(resolution)) {
    sectionSaveError.value = sectionInputMessage(resolution, addingClassroom.value?.name);
    return;
  }

  isAddingSection.value = true;
  try {
    if (resolution.kind === 'new') {
      await createSection({ name: resolution.name, classroom_ids: [String(classroomId)] });
    } else if (resolution.kind === 'existing') {
      await createClassroomSection({
        classroom_id: classroomId,
        section_id: resolution.section.id,
        name: classroomSectionName(addingClassroom.value?.name, resolution.section.name),
      });
    }
    await refetchSchool();
    cancelAddSection();
  } catch (e) {
    // The composable toasts too; keep the reason next to the field.
    sectionSaveError.value = extractGraphQLError(e, 'Could not add the section.');
  } finally {
    isAddingSection.value = false;
  }
};

// --- Edit Section State ---
const editingSectionId = ref<string | null>(null);
const editSectionName = ref('');
const isEditingSection = ref(false);

const startSectionEdit = (section: { id: string; name?: string | null; section?: { name?: string | null } | null }) => {
  editingSectionId.value = section.id;
  editSectionName.value = section.name || section.section?.name || '';
};

const cancelSectionEdit = () => {
  editingSectionId.value = null;
  editSectionName.value = '';
};

const saveSectionEdit = async(sectionId: string) => {
  const trimmed = editSectionName.value.trim();
  if (!trimmed) return;

  isEditingSection.value = true;
  try {
    await updateClassroomSection(sectionId, { name: trimmed });
    await refetchSchool();
    cancelSectionEdit();
  } catch (e) {
    // handled
  } finally {
    isEditingSection.value = false;
  }
};

</script>
